import { useRef, useState } from 'react';

import { useCancelSubscription, usePauseSubscription, useRecordRetentionFlowEvent } from '../models/billing/mutations';
import { PAUSE_MONTH_OPTIONS, nextStepAfterReason, resolveOffer } from '../utils/cancellationFlow';
import { canOfferPause } from '../utils/subscriptionState';

const offerShownForStep = (step, offerType) => {
	if (step === 'offer') return offerType;
	if (step === 'pause') return 'pause';
	return 'none';
};

/**
 * Step machine for the "Kündigen oder pausieren" modal: reason → one matching offer (or the pause step,
 * or straight to the confirmation) → done. Keeps a history stack for "Zurück" and remembers the first
 * offer screen that was rendered (`offerShown`) so the backend can attribute the outcome. Submitting
 * the reason step fires one fire-and-forget retention-flow event so abandoned flows stay visible
 * for the team's follow-up call. The submit
 * actions run the cancel/pause mutations; on failure the flow stays on its step and the global
 * MutationCache shows the toast. State is not reset — the modal is mounted only while open.
 *
 * @param {Object} params
 * @param {Object|null} params.subscription - GET /subscription payload; pause offers need `retention.canPause` plus at least one `pauseOptions` entry
 * @param {'reason'|'pause'} [params.initialStep='reason'] - 'pause' when opened via "Stattdessen pausieren"
 * @param {Function} [params.onClose]
 * @returns {{
 *   step: ('reason'|'offer'|'pause'|'confirm'|'done'),
 *   reason: (string|null),
 *   comment: string,
 *   pauseMonths: number,
 *   offer: { type: ('fullservice'|'call'|'pause'|null), pauseSecondary: boolean },
 *   pauseEligible: boolean,
 *   hasSeenPause: boolean,
 *   canGoBack: boolean,
 *   result: (null|{ kind: ('cancelled'|'paused'), subscription: Object }),
 *   isPending: boolean,
 *   setReason: Function,
 *   setComment: Function,
 *   setPauseMonths: Function,
 *   goNext: Function,
 *   goToPause: Function,
 *   goToConfirm: Function,
 *   declineOffer: Function,
 *   back: Function,
 *   submitCancel: Function,
 *   submitPause: Function,
 *   close: Function,
 * }}
 */
export function useCancellationFlow({ subscription, initialStep = 'reason', onClose }) {
	const [step, setStep] = useState(initialStep);
	const [history, setHistory] = useState([]);
	const [reason, setReason] = useState(null);
	const [comment, setComment] = useState('');
	const [pauseMonths, setPauseMonths] = useState(PAUSE_MONTH_OPTIONS[0]);
	const [offerShown, setOfferShown] = useState(offerShownForStep(initialStep, null));
	const [hasSeenPause, setHasSeenPause] = useState(initialStep === 'pause');
	const [result, setResult] = useState(null);

	const cancelMutation = useCancelSubscription();
	const pauseMutation = usePauseSubscription();
	const flowEventMutation = useRecordRetentionFlowEvent();
	const flowEventSent = useRef(false);

	const pauseEligible = canOfferPause(subscription?.retention);
	const offer = resolveOffer(reason, pauseEligible);

	const push = (next) => {
		setHistory((previous) => [...previous, step]);
		if (next === 'pause') setHasSeenPause(true);
		setStep(next);
	};

	const goNext = () => {
		if (!reason) return;
		const next = nextStepAfterReason(reason, pauseEligible);
		const firstOffer = offerShownForStep(next, offer.type);
		setOfferShown((previous) => (previous === 'none' ? firstOffer : previous));
		// Fire-and-forget churn signal: an abandoned flow stays visible for the team's follow-up call.
		if (!flowEventSent.current) {
			flowEventSent.current = true;
			flowEventMutation.mutate({
				reason,
				comment: comment.trim() || null,
				offerShown: firstOffer === 'none' ? null : firstOffer,
			});
		}
		push(next);
	};

	const goToPause = () => push('pause');

	const goToConfirm = () => push('confirm');

	// Declining a fullservice/call offer routes through the pause step once before the confirmation.
	const declineOffer = () => push(offer.pauseSecondary ? 'pause' : 'confirm');

	const back = () => {
		if (history.length === 0) return;
		setStep(history[history.length - 1]);
		setHistory((previous) => previous.slice(0, -1));
	};

	const buildPayload = () => ({
		reason,
		comment: comment.trim() || null,
		offerShown: offerShown === 'none' ? null : offerShown,
	});

	const submitCancel = async () => {
		if (!reason) return;
		try {
			const { data } = await cancelMutation.mutateAsync(buildPayload());
			setResult({ kind: 'cancelled', subscription: data.subscription });
			setStep('done');
		} catch {
			// Global mutation error handling surfaces the toast; the flow stays on its step.
		}
	};

	const submitPause = async () => {
		try {
			const { data } = await pauseMutation.mutateAsync({ months: pauseMonths, ...buildPayload() });
			setResult({ kind: 'paused', subscription: data.subscription });
			setStep('done');
		} catch {
			// Global mutation error handling surfaces the toast; the flow stays on its step.
		}
	};

	const close = () => onClose?.();

	return {
		step,
		reason,
		comment,
		pauseMonths,
		offer,
		pauseEligible,
		hasSeenPause,
		canGoBack: history.length > 0,
		result,
		isPending: cancelMutation.isPending || pauseMutation.isPending,
		setReason,
		setComment,
		setPauseMonths,
		goNext,
		goToPause,
		goToConfirm,
		declineOffer,
		back,
		submitCancel,
		submitPause,
		close,
	};
}
